import { z } from 'zod';
/**
 * Enum values are declared as `as const` tuples rather than inline in `z.enum`,
 * because Drizzle's `pgEnum` needs a tuple type. Declaring them once keeps the
 * Zod schema and the Postgres enum from drifting apart.
 */
/**
 * Every field type the attribute designer can produce.
 *
 * The iPhone app renders its capture form purely from these, so adding a value
 * here is the one change that DOES require an app update. Adding a *field* of
 * an existing type never does.
 */
export const ATTRIBUTE_TYPES = [
    'text',
    'longtext',
    'number',
    'decimal',
    'integer',
    'boolean',
    'date',
    'datetime',
    'select',
    'multiselect',
    'barcode',
    'photo',
    'user',
];
export const attributeTypeSchema = z.enum(ATTRIBUTE_TYPES);
/** Types whose value is one or more references into a value list. */
export const VALUE_LIST_BACKED_TYPES = ['select', 'multiselect'];
/** Types that hold more than one value. */
export const MULTI_VALUE_TYPES = ['multiselect', 'photo'];
/**
 * Where a field's value physically lives.
 *
 * - `column`   — a real column on `items`. Used where an index, a foreign key or
 *                both are needed: barcode lookup, supplier merge, sync ordering.
 * - `jsonb`    — a property in `items.attributes`. Everything the attribute
 *                designer creates lands here, which is what makes adding a price
 *                field later a pure data change: no migration, no deploy, no app
 *                update.
 * - `relation` — rows in a side table keyed by `attribute_key`. Only photo
 *                fields, whose values are files with their own upload lifecycle.
 *
 * All three are described by the same catalogue, so the designer, the iPhone
 * form builder and the export column picker read from one place.
 */
export const ATTRIBUTE_STORAGE_KINDS = ['column', 'jsonb', 'relation'];
export const attributeStorageSchema = z.enum(ATTRIBUTE_STORAGE_KINDS);
/**
 * Who may do what.
 *
 * `platform` reaches across tenants — it creates them and can help when one is
 * stuck. `admin` is the team lead of exactly one tenant: their team, their
 * fairs, their catalogue, their lists, and nothing outside. `capturer` captures.
 *
 * Ordered from most to least. Anything checking "at least admin" relies on it.
 */
export const USER_ROLES = ['platform', 'admin', 'capturer'];
export const userRoleSchema = z.enum(USER_ROLES);
/**
 * Lifecycle of a value list entry.
 *
 * `pending_approval` exists so a capturer can invent a supplier at the booth and
 * keep working. It becomes `active` on approval, or `merged` when an admin folds
 * it into an existing entry — at which point `merged_into_id` points at the
 * survivor and affected items are repointed.
 */
export const VALUE_LIST_ITEM_STATUSES = [
    'active',
    'pending_approval',
    'merged',
    'archived',
];
export const valueListItemStatusSchema = z.enum(VALUE_LIST_ITEM_STATUSES);
export const EXPORT_FORMATS = ['csv', 'json', 'zip'];
export const exportFormatSchema = z.enum(EXPORT_FORMATS);
export const EXPORT_ENCODINGS = ['utf-8', 'utf-8-bom', 'windows-1252'];
export const exportEncodingSchema = z.enum(EXPORT_ENCODINGS);
export const EXPORT_RUN_STATUSES = ['queued', 'running', 'succeeded', 'failed'];
export const exportRunStatusSchema = z.enum(EXPORT_RUN_STATUSES);
/** Upload lifecycle of a single photo. Local files are kept until `uploaded`. */
export const PHOTO_UPLOAD_STATUSES = ['pending', 'uploading', 'uploaded', 'failed'];
export const photoUploadStatusSchema = z.enum(PHOTO_UPLOAD_STATUSES);
export const SCHEMA_VERSION_STATUSES = ['draft', 'published'];
export const schemaVersionStatusSchema = z.enum(SCHEMA_VERSION_STATUSES);
//# sourceMappingURL=enums.js.map